/**
 * Landing.tsx — PIEA AGI marketing page
 * Matches AIEP Forecast design system exactly (aiep-ds.css tokens).
 * Shown to users who are not yet authenticated.
 * Classification: trade-secret/private
 */

import { useState } from "react";
import { IcoSun, IcoMoon } from "./Icons";
import { setApiKey } from "./api";

interface LandingProps {
    onSignIn: () => void;
    theme: "dark" | "light";
    onToggleTheme: () => void;
}

const FEATURES = [
    {
        title: "Three-Channel Reasoning",
        desc: "Every question is answered simultaneously through Verified, Theory, and Instinct channels — giving you the full epistemic picture, not just one answer.",
        iconBg: "rgba(37,99,235,0.12)",
        iconColor: "#60a5fa",
        icon: (
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <circle cx="8" cy="8" r="3.5" /><circle cx="8" cy="8" r="7" strokeDasharray="2 2" strokeOpacity=".5" />
            </svg>
        ),
    },
    {
        title: "Legal-Grade Evidence Chain",
        desc: "Every deliberation is hashed, timestamped, and committed to an immutable ledger. SHA-256 content integrity on all verdicts. Audit-ready by design.",
        iconBg: "rgba(34,197,94,0.10)",
        iconColor: "#34d399",
        icon: (
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M8 2L3 4.5v4C3 11.5 5.3 13.8 8 15c2.7-1.2 5-3.5 5-6.5v-4L8 2z" />
            </svg>
        ),
    },
    {
        title: "Real LLM Inference",
        desc: "Powered by DeepSeek-R1 for deep reasoning, Llama-3.3-70B for hypothesis competition, and Llama-3.2-3B for instant instinct signals — all via Cloudflare AI.",
        iconBg: "rgba(245,158,11,0.10)",
        iconColor: "#fbbf24",
        icon: (
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M9.5 2L4 9h5l-2.5 5L14 7H9l.5-5z" />
            </svg>
        ),
    },
    {
        title: "Curiosity Engine",
        desc: "After each deliberation, the substrate automatically identifies knowledge gaps — CAUSAL_UNCERTAINTY, TEMPORAL_DRIFT, DOMAIN_BLIND_SPOT — and prioritises them for investigation.",
        iconBg: "rgba(99,102,241,0.12)",
        iconColor: "#a5b4fc",
        icon: (
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M8 3C6 3 4.5 4.5 4.5 6.5c0 1.4.8 2.6 2 3.2V11h3v-1.3c1.2-.6 2-1.8 2-3.2C11.5 4.5 10 3 8 3z" /><path d="M6.5 13.5h3" />
            </svg>
        ),
    },
    {
        title: "Predictive Calibration",
        desc: "Every claim that can be tested becomes a prediction. The substrate tracks resolution, computes Brier scores, and calibrates its own confidence over time.",
        iconBg: "rgba(236,72,153,0.10)",
        iconColor: "#f9a8d4",
        icon: (
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M1.5 11.5L4.5 7l3 3.5L11 5l3.5 4" />
            </svg>
        ),
    },
    {
        title: "Self-Improvement (P612)",
        desc: "Governed recursive improvement. The substrate analyses its own reasoning quality, proposes refinements, and requires human approval before any change applies.",
        iconBg: "rgba(20,184,166,0.10)",
        iconColor: "#5eead4",
        icon: (
            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M13.5 6A6 6 0 1 0 14 8.5" /><path d="M13.5 2.5v4h-4" />
            </svg>
        ),
    },
];

const COMPLIANCE = [
    "SHA-256 verdict hash on every deliberation",
    "AIEP specification GB2519711.2 · P604",
    "Immutable Audit Ledger — append-only",
    "Multi-tenant data isolation by design",
    "GDPR-ready evidence erasure (Art.17)",
    "Cloudflare Workers — 99.9% uptime SLA",
];

export function Landing({ onSignIn, theme, onToggleTheme }: LandingProps) {
    const [showModal, setShowModal] = useState(false);
    const [keyInput, setKeyInput] = useState("");
    const [keyError, setKeyError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSignInClick = () => {
        setShowModal(true);
        setKeyInput("");
        setKeyError("");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const key = keyInput.trim();
        if (!key) { setKeyError("API key is required."); return; }
        setSubmitting(true);
        setKeyError("");
        try {
            setApiKey(key);
            onSignIn();
        } catch {
            setKeyError("Could not sign in. Check the key and try again.");
            setSubmitting(false);
        }
    };

    return (
        <div style={{ fontFamily: "'Inter', InterVariable, ui-sans-serif, sans-serif" }}>
            {/* ── Sign-in modal ── */}
            {showModal && (
                <div onClick={() => setShowModal(false)} style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit} style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 16, padding: 32, width: "100%", maxWidth: 440 }}>
                        <div style={{ fontFamily: "'Lexend', sans-serif", fontWeight: 700, fontSize: 18, color: "#fff", marginBottom: 6 }}>
                            Sign in to <span style={{ color: "#60a5fa" }}>PIEA AGI</span>
                        </div>
                        <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 24, lineHeight: 1.6 }}>
                            Enter your API key to access the Observatory. No key?{" "}
                            <a href="mailto:hello@piea.ai?subject=PIEA AGI access request" style={{ color: "#60a5fa" }}>Request access →</a>
                        </p>
                        <label style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.08em", textTransform: "uppercase", display: "block", marginBottom: 8 }}>API Key</label>
                        <input
                            autoFocus
                            type="password"
                            value={keyInput}
                            onChange={(e) => setKeyInput(e.target.value)}
                            placeholder="piea_xxxxxxxxxxxxxxxx"
                            style={{ width: "100%", boxSizing: "border-box", background: "#0f172a", border: `1px solid ${keyError ? "#ef4444" : "#334155"}`, borderRadius: 8, padding: "10px 14px", color: "#fff", fontSize: 14, fontFamily: "ui-monospace, monospace", outline: "none", marginBottom: keyError ? 6 : 20 }}
                        />
                        {keyError && <p style={{ fontSize: 12, color: "#ef4444", marginBottom: 16 }}>{keyError}</p>}
                        <div style={{ display: "flex", gap: 10 }}>
                            <button type="submit" disabled={submitting || !keyInput.trim()} style={{ flex: 1, padding: "10px 0", borderRadius: 8, background: "#2563eb", color: "#fff", border: "none", fontWeight: 700, fontSize: 14, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting || !keyInput.trim() ? 0.6 : 1 }}>
                                {submitting ? "Signing in…" : "Sign in"}
                            </button>
                            <button type="button" onClick={() => setShowModal(false)} style={{ padding: "10px 18px", borderRadius: 8, background: "transparent", color: "#64748b", border: "1px solid #334155", fontSize: 14, cursor: "pointer" }}>Cancel</button>
                        </div>
                    </form>
                </div>
            )}

            {/* ── Nav ── */}
            <nav className="ds-nav">
                <div className="ds-container ds-nav-inner">
                    <a className="ds-brand" href="#">
                        <span className="ds-brand-badge">AIEP</span>
                        <span className="ds-brand-name">PIEA AGI</span>
                    </a>
                    <div className="ds-nav-links">
                        <a className="ds-nav-link" href="mailto:hello@piea.ai">Contact</a>
                        <button className="ds-theme-btn" onClick={onToggleTheme} aria-label="Toggle theme" style={{ border: "1px solid var(--ds-border-card)", borderRadius: "var(--ds-r-md)", background: "transparent", cursor: "pointer", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ds-fg-muted)" }}>
                            {theme === "dark" ? <IcoSun size={14} /> : <IcoMoon size={14} />}
                        </button>
                        <button onClick={handleSignInClick} className="ds-btn ds-btn-primary ds-btn-sm">Sign in</button>
                    </div>
                </div>
            </nav>

            {/* ── Hero ── */}
            <div className="ds-hero" style={{ paddingTop: 120 }}>
                <div className="ds-hero-blob-1" />
                <div className="ds-hero-blob-2" />
                <div className="ds-hero-inner">
                    <div className="ds-hero-badge">GB2519711.2 · P604 · Cognitive reasoning substrate</div>
                    <h1 className="ds-hero-h1">
                        An AI that thinks,<br />
                        <span className="ds-hero-h1-accent">questions, and learns.</span>
                    </h1>
                    <p className="ds-hero-sub">
                        PIEA AGI is not a chatbot. It is a reasoning substrate that holds positions, tracks predictions, develops curiosity, and improves itself — continuously.
                    </p>
                    <div className="ds-hero-actions">
                        <button onClick={handleSignInClick} className="ds-btn ds-btn-primary ds-btn-lg">
                            Sign in to Observatory
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4" /></svg>
                        </button>
                        <a href="mailto:hello@piea.ai?subject=PIEA AGI demo request" className="ds-btn ds-btn-ghost ds-btn-lg">Request access</a>
                    </div>
                </div>

                {/* Mock browser */}
                <div className="ds-mock-browser" style={{ marginTop: 56 }}>
                    <div className="ds-mock-browser-bar">
                        <span className="ds-mock-dot ds-mock-dot-r" />
                        <span className="ds-mock-dot ds-mock-dot-y" />
                        <span className="ds-mock-dot ds-mock-dot-g" />
                        <span className="ds-mock-url">agi.piea.ai/observatory</span>
                    </div>
                    <div className="ds-mock-stats">
                        <div className="ds-mock-stat">
                            <span className="ds-mock-stat-val">14</span>
                            <span className="ds-mock-stat-lbl">Laws Indexed</span>
                            <span className="ds-mock-stat-sub">EU AI Act + GDPR + more</span>
                        </div>
                        <div className="ds-mock-stat">
                            <span className="ds-mock-stat-val">3</span>
                            <span className="ds-mock-stat-lbl">LLM Models</span>
                            <span className="ds-mock-stat-sub">parallel inference</span>
                        </div>
                        <div className="ds-mock-stat">
                            <span className="ds-mock-stat-val">100%</span>
                            <span className="ds-mock-stat-lbl">Evidenced</span>
                            <span className="ds-mock-stat-sub">SHA-256 every verdict</span>
                        </div>
                        <div className="ds-mock-stat">
                            <span className="ds-mock-stat-val">P604</span>
                            <span className="ds-mock-stat-lbl">AIEP Spec</span>
                            <span className="ds-mock-stat-sub">GB2519711.2</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Features ── */}
            <div className="ds-section ds-section-white">
                <div className="ds-container">
                    <div className="ds-section-hdr">
                        <h2 className="ds-section-h2">Everything reasoning should be</h2>
                        <p className="ds-section-sub">Six core capabilities — grounded in the AIEP specification and designed for AI systems that need to explain themselves.</p>
                    </div>
                    <div className="ds-card-grid ds-card-grid-3">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="ds-card ds-card-light">
                                <div className="ds-feature-icon" style={{ background: f.iconBg, color: f.iconColor }}>
                                    {f.icon}
                                </div>
                                <div className="ds-card-title">{f.title}</div>
                                <p className="ds-card-desc">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Compliance ── */}
            <div className="ds-section ds-section-slate">
                <div className="ds-container" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "center" }}>
                    <div>
                        <h2 className="ds-section-h2">Built for governance. Not bolted on.</h2>
                        <p className="ds-section-sub" style={{ margin: "16px 0 28px" }}>
                            From GDPR Article 17 erasure rights to the AIEP three-channel protocol, PIEA AGI is designed around the epistemic obligations AI systems carry — not retrofitted from generic AI tooling.
                        </p>
                        <ul className="ds-checklist">
                            {COMPLIANCE.map((item) => (
                                <li key={item}>
                                    <span className="ds-check-icon">
                                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M2.5 8.5l4 4 7-8" /></svg>
                                    </span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {[
                            { title: "Three-channel verdict", sub: "Verified answer, unverified theory, and instinct signal — surfaced simultaneously, never collapsed into one." },
                            { title: "Substrate identity chain", sub: "Every session advances a cryptographic identity chain. The substrate knows where it has been and can prove it." },
                            { title: "Human-gated improvement", sub: "Self-improvement proposals require explicit human approval. No model changes without a governance checkpoint." },
                        ].map((card) => (
                            <div key={card.title} style={{ display: "flex", gap: 14, padding: "18px 20px", background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12 }}>
                                <div style={{ width: 8, borderRadius: 4, background: "var(--ds-blue)", flexShrink: 0 }} />
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>{card.title}</div>
                                    <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>{card.sub}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Three channels explained ── */}
            <div className="ds-section ds-section-dark">
                <div className="ds-container">
                    <div className="ds-section-hdr">
                        <h2 className="ds-section-h2">The three-channel protocol</h2>
                        <p className="ds-section-sub">AIEP P136 — every deliberation runs three parallel reasoning channels, each with a distinct epistemic role.</p>
                    </div>
                    <div className="ds-card-grid ds-card-grid-3">
                        {[
                            {
                                label: "Verified", color: "#3b82f6", bg: "rgba(59,130,246,0.08)", border: "rgba(59,130,246,0.25)",
                                model: "DeepSeek-R1-Distill-Qwen-32B",
                                desc: "The highest-confidence channel. Evidence-grounded, references legislation and frameworks by name. Chain-of-thought reasoning surfaced via <think> processing."
                            },
                            {
                                label: "Theory", color: "#f59e0b", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.25)",
                                model: "Llama-3.3-70B-fp8-fast",
                                desc: "Alternative hypotheses and dissenting interpretations. Challenges mainstream views, surfaces contested assumptions. Deliberately takes a different position."
                            },
                            {
                                label: "Instinct", color: "#10b981", bg: "rgba(16,185,129,0.08)", border: "rgba(16,185,129,0.25)",
                                model: "Llama-3.2-3B",
                                desc: "Fast pattern recognition. 1-3 sentences. No hedging. The gut-level signal that practitioners know intuitively — surfaced in under 500ms."
                            },
                        ].map((ch) => (
                            <div key={ch.label} style={{ background: ch.bg, border: `1px solid ${ch.border}`, borderRadius: 18, padding: "28px 24px" }}>
                                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: ch.color, marginBottom: 12 }}>{ch.label}</div>
                                <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, color: "#475569", marginBottom: 14, padding: "4px 8px", background: "rgba(0,0,0,0.3)", borderRadius: 4, display: "inline-block" }}>{ch.model}</div>
                                <p style={{ fontSize: 13.5, color: "#94a3b8", lineHeight: 1.65 }}>{ch.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── CTA ── */}
            <div className="ds-cta-strip">
                <div className="ds-container">
                    <h2 className="ds-cta-h2">Ready to reason with evidence?</h2>
                    <p className="ds-cta-sub">Sign in to the Observatory — or contact us to set up your organisation.</p>
                    <div className="ds-cta-actions">
                        <button onClick={handleSignInClick} className="ds-btn ds-btn-primary ds-btn-lg">
                            Sign in
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 8h10M9 4l4 4-4 4" /></svg>
                        </button>
                        <a href="mailto:hello@piea.ai" className="ds-btn ds-btn-ghost ds-btn-lg" style={{ color: "#94a3b8", borderColor: "#334155" }}>Contact us</a>
                    </div>
                </div>
            </div>

            {/* ── Footer ── */}
            <footer className="ds-footer">
                <div className="ds-container">
                    <div className="ds-footer-apps">
                        <span style={{ fontSize: 11, color: "#475569" }}>AIEP ecosystem:</span>
                        <a href="https://aiepforecast.com" target="_blank" rel="noopener" className="ds-nav-link" style={{ fontSize: 12 }}>Forecast ↗</a>
                        <a href="https://aiep.dev" target="_blank" rel="noopener" className="ds-nav-link" style={{ fontSize: 12 }}>AIEP ↗</a>
                        <a href="https://phatfella.com" target="_blank" rel="noopener" className="ds-nav-link" style={{ fontSize: 12 }}>Phatfella ↗</a>
                    </div>
                    <div className="ds-footer-inner">
                        <div className="ds-brand" style={{ textDecoration: "none" }}>
                            <span className="ds-brand-badge">AIEP</span>
                            <span className="ds-brand-name">PIEA AGI</span>
                        </div>
                        <p className="ds-footer-copy">© {new Date().getFullYear()} AIEP. All rights reserved. · GB2519711.2</p>
                        <div className="ds-footer-links">
                            <a href="mailto:hello@piea.ai">Contact</a>
                            <button onClick={onSignIn} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#475569" }}>Sign in</button>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
